/**
 * Hulpfuncties voor klantprofiel (compleetheid + naam uit auth/metadata).
 */

import type { User } from '@supabase/supabase-js'
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js'
import type { AppProfile, ProfileGender } from '../services/profile'

export function getVoornaamAchternaam (
  user: User,
  profile: AppProfile | null
): { voornaam: string; achternaam: string } {
  const meta = user.user_metadata ?? {}
  const mv = typeof meta.voornaam === 'string' ? meta.voornaam.trim() : ''
  const ma = typeof meta.achternaam === 'string' ? meta.achternaam.trim() : ''
  if (mv || ma) return { voornaam: mv, achternaam: ma }

  const fullFromMeta = typeof meta.full_name === 'string' ? meta.full_name.trim() : ''
  const fromProfile = profile?.displayName?.trim() ?? ''
  const full = fromProfile || fullFromMeta
  if (!full) return { voornaam: '', achternaam: '' }
  const parts = full.split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { voornaam: '', achternaam: '' }
  if (parts.length === 1) return { voornaam: parts[0], achternaam: '' }
  return { voornaam: parts[0], achternaam: parts.slice(1).join(' ') }
}

/** Valideert een ingevoerd telefoonnummer (BE/NL-formaat of E.164). */
export function isGeldigKlantTelefoon (raw: string): boolean {
  const trimmed = raw.trim()
  if (!trimmed) return false
  try {
    const p = trimmed.startsWith('+') ? parsePhoneNumber(trimmed) : parsePhoneNumber(trimmed, 'BE')
    return p ? isValidPhoneNumber(p.number) : false
  } catch {
    return false
  }
}

function heeftGeldigTelefoon (user: User, profile: AppProfile | null): boolean {
  const raw =
    profile?.phone?.trim() ||
    (typeof user.user_metadata?.telefoon === 'string' ? user.user_metadata.telefoon.trim() : '') ||
    (typeof user.user_metadata?.phone === 'string' ? user.user_metadata.phone.trim() : '')
  return isGeldigKlantTelefoon(raw)
}

function isGeldigGender (g: ProfileGender | null | undefined): g is ProfileGender {
  return g === 'man' || g === 'vrouw' || g === 'anders' || g === 'liever_niet'
}

/** True als alle verplichte klantvelden zijn ingevuld. */
export function isKlantProfielCompleet (user: User, profile: AppProfile | null): boolean {
  const { voornaam, achternaam } = getVoornaamAchternaam(user, profile)
  if (!voornaam || !achternaam) return false
  if (!user.email?.trim()) return false
  if (!heeftGeldigTelefoon(user, profile)) return false
  if (!profile?.birthDate || !/^\d{4}-\d{2}-\d{2}$/.test(profile.birthDate)) return false
  if (!isGeldigGender(profile.gender)) return false
  return true
}

/** Doel-URL na login/wachtwoord voor pure klanten. */
export function getKlantPostAuthPath (user: User, profile: AppProfile | null): string {
  if (!isKlantProfielCompleet(user, profile)) {
    return '/klant/mijn-profiel?aanvullen=1'
  }
  return '/klant/mijn-boekingen'
}
